import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Check, X } from 'lucide-react';

export default function PasswordInput({ 
  value, 
  onChange, 
  placeholder = "Enter password",
  showValidation = false,
  id,
  name,
  required = false,
  disabled = false,
  className = "",
  "data-testid": dataTestId
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [validation, setValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false
  });

  useEffect(() => {
    if (showValidation && value) {
      setValidation({
        minLength: value.length >= 8,
        hasUppercase: /[A-Z]/.test(value),
        hasLowercase: /[a-z]/.test(value),
        hasNumber: /[0-9]/.test(value)
      });
    }
  }, [value, showValidation]);

  const isValid = validation.minLength && validation.hasUppercase && validation.hasLowercase && validation.hasNumber;
  const getStrength = () => {
    const checks = Object.values(validation).filter(Boolean).length;
    if (checks === 0) return { label: '', color: '' };
    if (checks <= 2) return { label: 'Weak', color: 'text-red-500' };
    if (checks === 3) return { label: 'Medium', color: 'text-yellow-500' };
    return { label: 'Strong', color: 'text-green-500' };
  };

  const strength = getStrength();

  const ValidationItem = ({ met, text }) => (
    <div className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-slate-400'}`}>
      {met ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
      {text}
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          id={id}
          name={name}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={`h-12 pr-10 ${className}`}
          data-testid={dataTestId}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>

      {showValidation && value && (
        <div className="space-y-2">
          {/* Strength indicator */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1 flex-1 mr-2">
              <div className={`h-1 flex-1 rounded ${validation.minLength ? 'bg-green-500' : 'bg-slate-200'}`} />
              <div className={`h-1 flex-1 rounded ${validation.hasUppercase ? 'bg-green-500' : 'bg-slate-200'}`} />
              <div className={`h-1 flex-1 rounded ${validation.hasLowercase ? 'bg-green-500' : 'bg-slate-200'}`} />
              <div className={`h-1 flex-1 rounded ${validation.hasNumber ? 'bg-green-500' : 'bg-slate-200'}`} />
            </div>
            {strength.label && (
              <span className={`text-xs font-medium ${strength.color}`}>{strength.label}</span>
            )}
          </div>

          {/* Requirements list */}
          <div className="grid grid-cols-2 gap-1">
            <ValidationItem met={validation.minLength} text="8+ characters" />
            <ValidationItem met={validation.hasUppercase} text="Uppercase letter" />
            <ValidationItem met={validation.hasLowercase} text="Lowercase letter" />
            <ValidationItem met={validation.hasNumber} text="Number" />
          </div>
        </div>
      )}
    </div>
  );
}

export function isPasswordValid(password) {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password)
  );
}
